import { useSyncExternalStore } from "react";
import { showcaseApi } from "../../http/showcase";
import {
  measureMaster,
  uploadMaster,
  UploadAbortedError,
} from "./masterUpload";

/**
 * Uploads in flight, outside React: a multi-GB master takes minutes, and the
 * person sending it will click around the CMS meanwhile. Unmounting a page
 * must not cancel the upload, so the state lives here and pages subscribe.
 * (Closing the TAB does cancel it; `beforeunload` warns while one runs.)
 */

export interface UploadState {
  videoId: string;
  fileName: string;
  phase: "measuring" | "uploading" | "finishing" | "done" | "failed";
  progress: number;
  error: string | null;
}

const uploads = new Map<string, UploadState>();
const controllers = new Map<string, AbortController>();
const listeners = new Set<() => void>();
let snapshot: ReadonlyMap<string, UploadState> = new Map();

function emit() {
  snapshot = new Map(uploads);
  listeners.forEach((l) => l());
}

function set(id: string, patch: Partial<UploadState>) {
  const cur = uploads.get(id);
  if (!cur) return;
  uploads.set(id, { ...cur, ...patch });
  emit();
}

const running = () =>
  [...uploads.values()].some(
    (u) =>
      u.phase === "measuring" ||
      u.phase === "uploading" ||
      u.phase === "finishing"
  );

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (!running()) return;
    e.preventDefault();
    e.returnValue = "";
  });
}

export function startUpload(
  videoId: string,
  file: File,
  onDone?: () => void
): void {
  controllers.get(videoId)?.abort();
  const controller = new AbortController();
  controllers.set(videoId, controller);
  uploads.set(videoId, {
    videoId,
    fileName: file.name,
    phase: "measuring",
    progress: 0,
    error: null,
  });
  emit();

  void (async () => {
    try {
      const measured = await measureMaster(file);
      const plan = await showcaseApi.beginUpload(videoId, file);
      set(videoId, { phase: "uploading" });
      const parts = await uploadMaster(
        file,
        plan,
        (progress) => set(videoId, { progress }),
        controller.signal
      );
      set(videoId, { phase: "finishing", progress: 1 });
      await showcaseApi.completeUpload(videoId, { parts, ...measured });
      set(videoId, { phase: "done" });
      onDone?.();
    } catch (e) {
      if (e instanceof UploadAbortedError) {
        await showcaseApi.abortUpload(videoId).catch(() => {});
        uploads.delete(videoId);
        emit();
        return;
      }
      set(videoId, {
        phase: "failed",
        error:
          (e as { body?: { message?: string } })?.body?.message ??
          (e instanceof Error ? e.message : String(e)),
      });
    } finally {
      controllers.delete(videoId);
    }
  })();
}

export function cancelUpload(videoId: string): void {
  controllers.get(videoId)?.abort();
}

export function useUploads(): ReadonlyMap<string, UploadState> {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot
  );
}
